import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import styles from './RolesPage.module.css';

type Staff = { id: string; name: string; email: string; role: 'super_admin' | 'operator'; approver: boolean; granted_by: string | null; granted_at: string | null };

/** The PO approver permission (Warren, 21 October 2026): any Super Admin grants or revokes it for EVI's admin logins;
 * the server audits each change and refuses to revoke the last approver. */
export function PoApproversCard() {
  const isSuperAdmin = useAuthStore(s => s.user?.role === 'super_admin');
  const [staff, setStaff] = useState<Staff[] | null>(null), [pick, setPick] = useState(''), [busy, setBusy] = useState(false), [message, setMessage] = useState('');
  const load = async () => {
    try { setStaff((await api.get('admin/po-approvers').json<{ data: { staff: Staff[] } }>()).data.staff); }
    catch { setStaff(null); }
  };
  useEffect(() => { if (isSuperAdmin) void load(); }, [isSuperAdmin]);
  if (!isSuperAdmin || !staff) return null;
  const change = async (userId: string, grant: boolean) => {
    setBusy(true); setMessage('');
    try {
      await api.post('admin/po-approvers', { json: { user_id: userId, grant } });
      await load(); setPick(''); setMessage(grant ? 'PO approver permission granted.' : 'PO approver permission revoked.');
    } catch (err) {
      const body = await (err as { response?: Response }).response?.json().catch(() => null) as { error?: { message?: string } } | null;
      setMessage(body?.error?.message ?? 'Not saved.');
    } finally { setBusy(false); }
  };
  const approvers = staff.filter(s => s.approver), others = staff.filter(s => !s.approver);
  return <section className={styles.list} aria-labelledby="po-approvers-title" style={{ marginBottom: 20 }}>
    <h2 id="po-approvers-title" className={styles.rowName} style={{ fontSize: 16 }}>PO approvers</h2>
    <p className={styles.hint}>A PO approver approves freight quotes, purchase orders, serial matches and marks a PO sent, on any job whose sales team they are not on. Each one gets the email and the bell for what waits; the first to act wins. Approving also needs the Super Admin role.</p>
    {approvers.map(s => <div key={s.id} className={styles.row}>
      <div className={styles.rowMain}><span className={styles.rowName}>{s.name || s.email}</span>
        <span className={styles.rowMeta}>{s.email}{s.role === 'operator' ? ' · Operator: approves once a Super Admin' : ''}{s.granted_by ? ` · granted by ${s.granted_by}${s.granted_at ? `, ${new Date(s.granted_at).toLocaleDateString()}` : ''}` : ''}</span></div>
      <div className={styles.rowAside}><button type="button" className={styles.dangerBtn} disabled={busy} onClick={() => void change(s.id, false)}>Revoke</button></div>
    </div>)}
    {others.length > 0 && <div className={styles.filterRow}>
      <select className={styles.select} value={pick} onChange={e => setPick(e.target.value)} aria-label="Grant the PO approver permission to">
        <option value="">Grant to an admin login</option>
        {others.map(s => <option key={s.id} value={s.id}>{s.name || s.email} ({s.role === 'super_admin' ? 'Super Admin' : 'Operator'})</option>)}
      </select>
      <button type="button" className={styles.primaryBtn} disabled={busy || !pick} onClick={() => void change(pick, true)}>Grant</button>
    </div>}
    <p className={styles.hint} role="status">{message}</p>
  </section>;
}
